import React from 'react';
import { Box, Paper, Typography, Stack, Divider } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import useCurrency from '../../../hooks/common/useCurrency';
import ReturnStatusBadge from '../Table/ReturnStatusBadge';

const Field = ({ label, value }) => (
    <Box>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="body2" fontWeight="medium">{value || '-'}</Typography>
    </Box>
);

const ReturnHeader = ({ data }) => {
    const { i18n } = useTranslation();
    const isArabic = (i18n.language || 'en').startsWith('ar');
    const { format: formatPrice } = useCurrency();

    const orderNumber = data?.order?.order_number || data?.order_number;
    const orderId = data?.order?.id || data?.order_id;
    const requestedAt = data?.requested_at || data?.created_at;

    return (
        <Paper
            elevation={0}
            sx={{
                p: { xs: 2.5, md: 3.5 },
                borderRadius: 3,
                border: (t) => `1px solid ${t.palette.divider}`,
                mb: 3,
            }}
        >
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
                <Box>
                    <Stack direction="row" spacing={1.5} alignItems="center" mb={0.5}>
                        <Typography variant="h5" fontWeight={700}>
                            {isArabic ? 'مرتجع' : 'Return'} #{data?.return_number || data?.id}
                        </Typography>
                        <ReturnStatusBadge status={data?.status} />
                    </Stack>
                    {requestedAt && (
                        <Typography variant="body2" color="text.secondary">
                            {isArabic ? 'تم الطلب:' : 'Requested:'} {dayjs(requestedAt).format('YYYY-MM-DD HH:mm')}
                        </Typography>
                    )}
                </Box>

                <Box sx={{ textAlign: { xs: 'start', md: 'end' } }}>
                    <Typography variant="caption" color="text.secondary">
                        {isArabic ? 'قيمة الاسترداد' : 'Refund Amount'}
                    </Typography>
                    <Typography variant="h5" fontWeight={700} color="primary.main">
                        {formatPrice(data?.refund_amount ?? data?.total_refund ?? 0)}
                    </Typography>
                </Box>
            </Stack>

            <Divider sx={{ my: 2.5 }} />

            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={3}
                divider={<Divider orientation="vertical" flexItem />}
                flexWrap="wrap"
            >
                <Field
                    label={isArabic ? 'الطلب الأصلي' : 'Original Order'}
                    value={
                        orderNumber ? (
                            <Typography
                                component={RouterLink}
                                to={`/orders/${orderId}`}
                                sx={{ color: 'primary.main', textDecoration: 'none' }}
                                variant="body2"
                                fontWeight="medium"
                            >
                                {orderNumber}
                            </Typography>
                        ) : '-'
                    }
                />
                <Field
                    label={isArabic ? 'العميل' : 'Customer'}
                    value={data?.user?.name || data?.customer?.name || data?.order?.user?.name}
                />
                <Field
                    label={isArabic ? 'البريد الإلكتروني' : 'Email'}
                    value={data?.user?.email || data?.order?.user?.email}
                />
                <Field
                    label={isArabic ? 'حالة الاسترداد' : 'Refund Status'}
                    value={data?.refund_status || '-'}
                />
            </Stack>
        </Paper>
    );
};

export default ReturnHeader;
